import { createSlice } from '@reduxjs/toolkit';
import { checkPhone, registerUser, loginUser, getCurrentUser, updateProfile } from '../apis/AuthApi';

const initialState = {
  user: null,
  isAuthenticated: false,
  loading: false,
  error: null,
  success: null,
  phoneExists: null, // Track if phone exists for registration flow
  pendingPhone: null // Store phone number during registration
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
    clearPhoneCheck: (state) => {
      state.phoneExists = null;
      state.pendingPhone = null;
    },
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.phoneExists = null;
      state.pendingPhone = null;
      localStorage.removeItem('memberToken');
      localStorage.removeItem('isAuthenticated');
    }
  },
  extraReducers: (builder) => {
    // Check Phone
    builder
      .addCase(checkPhone.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkPhone.fulfilled, (state, action) => {
        state.loading = false;
        // API: {success, message, data:{exists, user?, token?, phone?}}
        const d = action.payload?.data || action.payload;
        if (d?.exists) {
          state.user = d.user;
          state.isAuthenticated = true;
          state.success = action.payload.message;
          if (d.token) {
            localStorage.setItem('memberToken', d.token);
            localStorage.setItem('isAuthenticated', 'true');
          }
        } else {
          state.phoneExists = false;
          state.pendingPhone = d?.phone;
          state.success = null;
        }
      })
      .addCase(checkPhone.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to check phone';
      });

    // Register
    builder
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        const d = action.payload?.data || action.payload;
        state.user = d?.user;
        state.isAuthenticated = true;
        state.success = action.payload.message;
        state.phoneExists = null;
        state.pendingPhone = null;
        if (d?.token) {
          localStorage.setItem('memberToken', d.token);
          localStorage.setItem('isAuthenticated', 'true');
        }
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Registration failed';
      });

    // Login
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        const d = action.payload?.data || action.payload;
        state.user = d?.user;
        state.isAuthenticated = true;
        state.success = action.payload.message;
        if (d?.token) {
          localStorage.setItem('memberToken', d.token);
          localStorage.setItem('isAuthenticated', 'true');
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Login failed';
      });

    // Get Current User
    builder
      .addCase(getCurrentUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload?.data?.user || action.payload?.user || null;
        state.isAuthenticated = true;
      })
      .addCase(getCurrentUser.rejected, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        // Clear token so App.js doesn't retry on next mount with a dead token
        localStorage.removeItem('memberToken');
        localStorage.removeItem('isAuthenticated');
      });

    // Update Profile
    builder
      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload?.data?.user || action.payload?.user || state.user;
        state.success = action.payload.message;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to update profile';
      });
  }
});

export const { clearError, clearSuccess, clearPhoneCheck, logout } = authSlice.actions;
export default authSlice.reducer;
